/**
 * 품목 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
 * 품목 코드 더블클릭으로 품목 검색 모달을 여는 기능 제공
 */

import { useCallback, useMemo } from "react";
import { CreateCellDoubleClickHandler, CustomCellDoubleClickAction } from "src/utils/grid/GridHandlerContent";
import { showWarning } from "src/utils/toast/toastUtil";
import { 
  ItemDetailDto, 
  ItemSearchDetailTableEventsProps, 
  ItemSearchHandlers 
} from "src/types/detail-table-new";
import { useDetailTableEvents } from "./useDetailTableEvents";

export function useDetailTableWithItemSearch<T extends ItemDetailDto>({
  detailList,
  editableColumns = [],
  itemSearchConfig,
  constraints,
  keyDownConfig,
}: ItemSearchDetailTableEventsProps<T>): ItemSearchHandlers<T> {

  // 기본 핸들러들 가져오기
  const basicHandlers = useDetailTableEvents({
    detailList,
    editableColumns,
    keyDownConfig,
  });

  /**
   * 품목 검색 더블클릭 핸들러 생성
   */
  const cellDoubleClickActions: CustomCellDoubleClickAction<T>[] = useMemo(() => {
    const actions: CustomCellDoubleClickAction<T>[] = [];

    // 제약 조건 체크 함수 (foundData 파라미터 추가)
    const isActionAllowed = (foundData?: T): boolean => {

      if (constraints?.notTransport === false) {
        showWarning('출하정보가 있는 데이터는 수정이 불가능합니다.');
        return false;
      }

      // notEdit 을 받아와서 순수 boolean인지 boolean 반환 함수인지 탐지 (boolean 또는 function)
      // 순수 boolean든 boolean 반환 함수든 값이 false면 편집 불가.
      const notEditConstraint = constraints?.notEdit;
      
      let isNotEditable = false;

      if (typeof notEditConstraint === 'function') {
        // 함수인 경우 실행. params 형태로 각 row의 데이터 전달
        isNotEditable = notEditConstraint({ data: foundData });
      } else if (typeof notEditConstraint === 'boolean') {
        // boolean인 경우 값 사용
        isNotEditable = notEditConstraint;
      }

      if (isNotEditable === false) {
        showWarning('편집이 불가능한 상태입니다.');
        return false;
      }
      return true;
    };

    const config = itemSearchConfig.config;

    // itemCd 필드 더블클릭 (기존 값으로 검색)
    actions.push({
      validate: (field) => field === 'itemCd',
      action: (e, foundData) => {
        if (!isActionAllowed(foundData)) return; // foundData 전달
        
        if (itemSearchConfig.settingCodeSearchModalProps) {
          itemSearchConfig.settingCodeSearchModalProps(
            config.category,
            (foundData as any).itemCd || "",
            false,
            'itemCd',
            foundData,
            foundData.valueIndex
          );
        }
      }
    });

    // 관련 필드들 더블클릭 (빈 값으로 검색)
    Object.values(config.responseMapping).forEach(targetField => {
      if (targetField !== 'itemCd') {
        actions.push({
          validate: (field) => field === targetField,
          action: (e, foundData) => {
            if (!isActionAllowed(foundData)) return; // foundData 전달
            
            if (itemSearchConfig.settingCodeSearchModalProps) {
              itemSearchConfig.settingCodeSearchModalProps(
                config.category,
                "",
                false,
                'itemCd',
                foundData,
                foundData.valueIndex
              );
            }
          }
        });
      }
    });

    return actions;
  }, [itemSearchConfig, constraints]);

  const handleCellDoubleClick = CreateCellDoubleClickHandler(detailList, 'valueIndex', cellDoubleClickActions);

  // 키다운 핸들러 확장 (품목 검색 특화)
  const enhancedHandleCellKeyDown = useCallback((event: any) => {
    // 기본 키다운 핸들러 실행
    basicHandlers.handleCellKeyDown(event);

    // 품목 검색 특화 키다운 로직 추가 가능
    // 엔터 키로 품목 검색 모달 열기 (품목 코드가 비어있을 때만)
    if (event.event.key === 'Enter' && event.colDef.field === 'itemCd') {
      const foundData = detailList.find(item => item.valueIndex === event.data.valueIndex);
      if (foundData) {
        // 제약 조건 체크
        if (constraints?.notTransport === false) {
          showWarning('출하정보가 있는 데이터는 수정이 불가능합니다.');
          return;
        }
        
        // 품목 코드가 비어있을 때만 모달 열기
        const currentItemCd = (foundData as any).itemCd;
        if (!currentItemCd || currentItemCd.trim() === '') {
          if (itemSearchConfig.settingCodeSearchModalProps) {
            itemSearchConfig.settingCodeSearchModalProps(
              itemSearchConfig.config.category,
              "",
              false,
              'itemCd',
              foundData,
              foundData.valueIndex
            );
          }
        }
        // 품목 코드가 있으면 기본 네비게이션 로직 실행 (다음 셀로 이동)
      }
    }
  }, [basicHandlers.handleCellKeyDown, detailList, itemSearchConfig, constraints]);

  return {
    ...basicHandlers,
    handleCellKeyDown: enhancedHandleCellKeyDown,
    handleCellDoubleClick,
  };
}
